import { SubmitHandler, useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  AlertMessage,
  Flex,
  Form,
  FormButtons,
  Input,
  NormalizedException,
  useRsaContext,
} from "@songtradr-sso/shared/frontend";
import {
  A,
  Alert,
  Body,
  Button,
  ButtonLoadingIcon,
  Heading,
} from "@songtradr/design-language";
import { forwardAuth } from "../../../shared/utils/api.utils";
import {
  FormFields,
  emailPasswordSignIn,
  routes,
  schema,
  text,
} from "./EmailPasswordSignIn.Parts";

export const EmailPasswordSignIn = () => {
  const navigate = useNavigate();
  const { rsaEncrypt } = useRsaContext();

  const storedEmail = sessionStorage.getItem("email") ?? "";

  const formMethods = useForm<FormFields>({
    mode: "onSubmit",
    resolver: zodResolver(schema),
    defaultValues: { [text.shared.inputs.email.id]: storedEmail },
  });

  const {
    formState: { errors, isSubmitting },
    setError,
  } = formMethods;

  const onSubmit: SubmitHandler<FormFields> = async (data) => {
    try {
      if (storedEmail) sessionStorage.removeItem("email");
      const rsaEncryptedRequest = await rsaEncrypt(JSON.stringify(data));
      const redirectUrl = await emailPasswordSignIn(rsaEncryptedRequest);
      if (redirectUrl) await navigate(redirectUrl);
      else await forwardAuth();
    } catch (err) {
      const error = new NormalizedException(err);
      if (error.code === "SignIn.UserNotConfirmedException") {
        sessionStorage.setItem("email", data.email);
        await navigate(routes.confirmEmail);
      }
      setError("root", { message: error.message });
    }
  };

  return (
    <Form formMethods={formMethods} onSubmit={onSubmit}>
      <Flex $justify="space-between">
        <Heading variant="section">{text.title}</Heading>
        <Body variant="small">
          <A as={Link} to={routes.signUp} $disabled={isSubmitting}>
            {text.buttons.signUp}
          </A>
        </Body>
      </Flex>
      <Input
        id={text.shared.inputs.email.id}
        label={text.shared.inputs.email.label}
      />
      <Input
        type="password"
        id={text.shared.inputs.password.currentPassword.id}
        label={text.shared.inputs.password.label}
      />
      <Alert message={errors.root?.message} dismissible />
      {storedEmail && (
        <AlertMessage
          isFormValid={true}
          successMessage={text.alerts.signUpSuccess}
        />
      )}
      <FormButtons $justify="space-between">
        <A
          as={Link}
          to={routes.password.forgotPassword}
          $disabled={isSubmitting}
        >
          {text.buttons.recovery}
        </A>
        <Button $variant="important" type="submit" disabled={isSubmitting}>
          {isSubmitting ? <ButtonLoadingIcon /> : text.buttons.submit}
        </Button>
      </FormButtons>
    </Form>
  );
};
